'use client';
import { CarouselProvider, Slider, ButtonBack, ButtonNext } from 'pure-react-carousel';
import LogoSlide from './_LogoSlide';
import { useIsLG, useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import LeftArrow from '@repo/ui/src/components/Carousels/CarouselArrows/left-arrow';
import RightArrow from '@repo/ui/src/components/Carousels/CarouselArrows/right-arrow';
import { useMemo } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MediaType, LinkProps } from '@repo/ui/src/types';

interface LogoCarouselProps {
  items?: Array<{
    image: MediaType;
    link: LinkProps;
  }>;
  header: string;
  title?: string;
  description?: string;
}

const LogoCarousel = ({ items = [], header, title, description }: LogoCarouselProps) => {
  const carouselTitle = header || title;
  const isLarge = useIsLG();
  const isMed = useIsMD();
  const logoOnly = !!items;

  const slideCount = useMemo(() => {
    if (isLarge && items?.length >= 5) {
      return 5;
    }

    if (isLarge && items?.length < 5) {
      return 4;
    }
    if (isMed) {
      return 3;
    }
    return 1;
  }, [isMed, isLarge, items]);

  const hideArrows = slideCount === items?.length;

  if (!items.length) return null;

  return (
    <section className="tw-flex tw-flex-col tw-text-center tw-my-10 tw-mx-5 md:tw-my-20 lg:tw-mx-auto lg:tw-max-w-[1200px]">
      <h4 className="tra-text-web-heading-4 tw-pb-10">{carouselTitle}</h4>
      {description && <p className="tra-text-web-body-m tw-pb-10">{description}</p>}
      <p className="sr-only">This is a carousel. Tab through slides to navigate.</p>
      <CarouselProvider
        naturalSlideWidth={100}
        naturalSlideHeight={logoOnly ? 75 : 125}
        totalSlides={items.length}
        visibleSlides={slideCount}
        className={classNames('tw-grid', !hideArrows && 'md:tw-grid-cols-[60px_auto_60px]')}
        infinite
      >
        <ButtonBack
          className={classNames(
            'tw-hidden tw-h-fit tw-self-center md:tw-block',
            hideArrows && 'md:tw-hidden',
          )}
        >
          <LeftArrow asSpan />
        </ButtonBack>
        <Slider>{items?.map((slide, i) => <LogoSlide slide={slide} key={i} index={i} />)}</Slider>
        <ButtonNext
          className={classNames(
            'tw-hidden tw-h-fit tw-self-center md:tw-block',
            hideArrows && 'md:tw-hidden',
          )}
        >
          <RightArrow asSpan />
        </ButtonNext>
        <div
          className={classNames(
            'tw-flex tw-flex-row tw-w-full tw-justify-center tw-gap-5',
            hideArrows && 'tw-hidden',
          )}
        >
          <ButtonBack className="tw-block tw-h-fit tw-self-center md:tw-hidden">
            <LeftArrow asSpan />
          </ButtonBack>
          <ButtonNext className="tw-h-fit tw-self-center md:tw-hidden">
            <RightArrow asSpan />
          </ButtonNext>
        </div>
      </CarouselProvider>
    </section>
  );
};

export default LogoCarousel;
